import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, Clock, Landmark, QrCode, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { ActionDot, useUnreadNotifications } from "@/components/layout/ActionDot";
import { api, errorMessage, fetcher } from "@/lib/api";
import { formatDate, ROLE_LABELS, rupiah, SETTLEMENT_STATUS } from "@/lib/labels";

const uang = (v) => (v === undefined || v === null ? "—" : rupiah(v));

const Chip = ({ tone = "muted", children, testId }) => {
  const warna = {
    muted: "border-border bg-muted/40 text-muted-foreground",
    warn: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
    ok: "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100",
    info: "border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100",
  }[tone];
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${warna}`} data-testid={testId}>{children}</span>;
};

// Satu layar satu pekerjaan: reseller mengonfirmasi uang masuk dari sekolah binaan,
// lalu menyetor kulakan Rp2.000/akun ke founder memakai QRIS statis di layar ini.
// Konfirmasi tidak langsung mengaktifkan akun: permintaannya diteruskan ke founder.
export default function MitraPembayaran() {
  const qc = useQueryClient();
  const belumDibaca = useUnreadNotifications();
  const [periode, setPeriode] = useState("");
  const q = useQuery({
    queryKey: ["mitra", "pembayaran", periode],
    queryFn: () => fetcher(`/reseller/pembayaran${periode ? `?periode=${periode}` : ""}`),
    refetchInterval: 15000,
  });
  const [schoolId, setSchoolId] = useState("");
  const [jumlah, setJumlah] = useState("");
  const [metode, setMetode] = useState("qris");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [konfirmasiUntuk, setKonfirmasiUntuk] = useState(null);
  const [sudahCocok, setSudahCocok] = useState(false);
  const [dipilih, setDipilih] = useState([]);
  const [hasilKonfirmasi, setHasilKonfirmasi] = useState(null);

  if (q.isLoading) return <LoadingState rows={6} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const g = d.gambaran;
  const f = d.kanal_founder || {};
  const periodeList = [...new Set([d.periode, ...(d.periode_tersedia || [])])].sort().reverse();
  const adaKlaim = d.sekolah.some((s) => s.menunggu_konfirmasi > 0);
  const adaAktivasi = d.sekolah.some((s) => s.menunggu_aktivasi > 0);
  const founderKanal = [f.qris_label && `QRIS ${f.qris_label}`, f.qris_nmid, f.bank_account && `Rekening ${f.bank_account}`]
    .filter(Boolean).join(" · ");
  const perSekolah = g.per_sekolah || [];
  const sekolahTerpilih = perSekolah.find((s) => s.school_id === schoolId);
  const sisaSekolah = sekolahTerpilih ? Math.max(0, sekolahTerpilih.nominal_kulakan - (sekolahTerpilih.sudah_disetor || 0)) : 0;
  const tagihanSekolah = konfirmasiUntuk
    ? (d.tagihan || []).filter((b) => b.school_id === konfirmasiUntuk.school_id) : [];
  const akunMelapor = tagihanSekolah.filter((b) => b.status === "CLAIMED");
  const akunSudahDikonfirmasi = tagihanSekolah.filter((b) => b.status === "CONFIRMED");
  const akunDipilih = akunMelapor.filter((b) => dipilih.includes(b.id));
  const nilaiDipilih = akunDipilih.reduce((n, b) => n + (b.retail_price_idr || 0), 0);
  const semuaDipilih = akunMelapor.length > 0 && akunDipilih.length === akunMelapor.length;
  const jumlahAlur = (kunci) => d.sekolah.reduce((n, s) => n + (s[kunci] || 0), 0);
  const alur = [
    { label: "Belum bayar", nilai: jumlahAlur("menunggu_bayar"), tone: "muted" },
    { label: "Menunggu konfirmasi Anda", nilai: jumlahAlur("menunggu_konfirmasi"), tone: "info" },
    { label: "Menunggu aktivasi founder", nilai: jumlahAlur("menunggu_aktivasi"), tone: "warn" },
    { label: "Aktif", nilai: jumlahAlur("aktif"), tone: "ok" },
  ];
  const klaimBaru = (nama) => belumDibaca.some((n) => n.kind === "billing_claim" && (n.body || "").includes(nama));

  const reload = async () => {
    setJumlah("");
    setCatatan("");
    await qc.invalidateQueries({ queryKey: ["mitra", "pembayaran"] });
    await qc.invalidateQueries({ queryKey: ["mitra", "beranda"] });
  };

  const bukaKonfirmasi = (s) => {
    setErr("");
    setSudahCocok(false);
    setDipilih((d.tagihan || [])
      .filter((b) => b.school_id === s.school_id && b.status === "CLAIMED")
      .map((b) => b.id));
    setKonfirmasiUntuk(s);
  };

  const tutupKonfirmasi = () => {
    setKonfirmasiUntuk(null);
    setDipilih([]);
    setSudahCocok(false);
    setErr("");
  };

  const toggleAkun = (id) => setDipilih((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleSemua = () => setDipilih(semuaDipilih ? [] : akunMelapor.map((b) => b.id));

  const konfirmasi = async () => {
    const s = konfirmasiUntuk;
    if (!s) return;
    if (!dipilih.length) return setErr("Pilih minimal satu akun yang uangnya sudah masuk.");
    setBusy(s.school_id);
    setErr("");
    try {
      const { data } = await api.post("/reseller/pembayaran/konfirmasi", { school_id: s.school_id, bill_ids: dipilih });
      setDipilih([]);
      setKonfirmasiUntuk(null);
      setHasilKonfirmasi({
        school_name: s.school_name,
        dikonfirmasi: data.dikonfirmasi,
        nominal_jual: data.nominal_jual ?? s.nilai_menunggu_konfirmasi,
        periode: data.periode,
      });
      await reload();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy("");
    }
  };

  const setor = async () => {
    const n = Number(String(jumlah).replace(/\D/g, ""));
    if (!n || n <= 0) return setErr("Isi jumlah setoran dengan angka rupiah, contoh 40000.");
    if (!schoolId) return setErr("Pilih sekolah dulu sebelum menyetor.");
    setBusy("setor");
    setErr("");
    try {
      await api.post("/reseller/setoran", { periode: d.periode, school_id: schoolId, amount_idr: n, metode, catatan: catatan || undefined });
      toast.success(`Laporan setoran untuk ${sekolahTerpilih.school_name} terkirim. Founder akan memverifikasi.`);
      setSchoolId("");
      await reload();
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Ruang Mitra SIST" title="Kelola Pembayaran"
        description="Konfirmasi pembayaran sekolah binaan, lalu setor kulakan ke founder memakai QRIS di bawah. Harga kulakan dan setoran adalah urusan Anda dengan founder — sekolah tidak melihat bagian ini."
        actions={
          <Select value={d.periode} onValueChange={setPeriode}>
            <SelectTrigger aria-label="Periode" className="w-40" data-testid="mitra-payment-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{periodeList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        } />

      {err && <Notice tone="danger" testId="mitra-payment-error">{err}</Notice>}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-akun">
          <p className="font-brand text-2xl font-extrabold tabular-nums">{g.akun}</p>
          <p className="text-xs text-muted-foreground">Akun ditagih</p>
        </div>
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-dibayar">
          <p className="font-brand text-2xl font-extrabold tabular-nums text-emerald-600">{g.akun_dibayar_ke_reseller}</p>
          <p className="text-xs text-muted-foreground">Dilaporkan bayar oleh sekolah</p>
        </div>
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-kulakan">
          <p className="font-brand text-2xl font-extrabold tabular-nums">{uang(g.nominal_kulakan)}</p>
          <p className="text-xs text-muted-foreground">Kulakan {uang(g.wholesale_price_idr)}/akun</p>
        </div>
        <div className="rounded-2xl border bg-card p-4" data-testid="mitra-payment-sisa">
          <p className="font-brand text-2xl font-extrabold tabular-nums text-[#E11D2E]">{uang(g.sisa_kulakan)}</p>
          <p className="text-xs text-muted-foreground">Sisa kulakan ke founder</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border bg-card p-5 space-y-4" data-testid="mitra-payment-confirm-section">
          <h2 className="flex items-center gap-2 text-base font-bold">
            <BadgeCheck className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Konfirmasi pembayaran sekolah
          </h2>

          <div className="rounded-xl border bg-muted/30 p-3" data-testid="mitra-payment-flow">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Alur akun periode {d.periode}</p>
            <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {alur.map((a, i) => (
                <li key={a.label} className="space-y-1" data-testid={`mitra-flow-${i}`}>
                  <Chip tone={a.tone}>{a.nilai} akun</Chip>
                  <p className="text-xs leading-tight text-muted-foreground">{a.label}</p>
                </li>
              ))}
            </ol>
            <p className="mt-3 text-xs text-muted-foreground">
              Konfirmasi Anda hanya berarti uang dari sekolah sudah masuk ke Anda. Akun baru aktif setelah founder
              menyetujui aktivasi di Pusat Kendali.
            </p>
          </div>

          {!adaKlaim && !adaAktivasi && <p className="text-sm text-muted-foreground">Tidak ada laporan bayar yang menunggu konfirmasi pada periode ini.</p>}
          {d.sekolah.map((s) => (
            <div key={s.school_id} className="rounded-xl border p-4 space-y-3" data-testid={`mitra-payment-school-${s.school_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{s.school_name}</p>
                  <p className="text-xs text-muted-foreground">{s.akun} akun · nilai jual {uang(s.nominal_jual)}</p>
                </div>
                {s.menunggu_konfirmasi > 0 ? (
                  <Button size="sm" onClick={() => bukaKonfirmasi(s)}
                    data-testid={`mitra-payment-confirm-${s.school_id}`}>
                    <BadgeCheck className="h-4 w-4 mr-1.5" />
                    Konfirmasi pembayaran {s.menunggu_konfirmasi} akun
                    <ActionDot show={klaimBaru(s.school_name)} testId={`mitra-payment-confirm-dot-${s.school_id}`}
                      className="ml-1" />
                  </Button>
                ) : s.menunggu_aktivasi > 0 ? (
                  <Chip tone="warn" testId={`mitra-payment-waiting-${s.school_id}`}>
                    <Clock className="inline h-3 w-3 mr-1" aria-hidden />Menunggu aktivasi founder
                  </Chip>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-1.5">
                <Chip tone="muted">Belum bayar {s.menunggu_bayar}</Chip>
                <Chip tone="info">Menunggu konfirmasi Anda {s.menunggu_konfirmasi}</Chip>
                <Chip tone="warn">Menunggu aktivasi founder {s.menunggu_aktivasi}</Chip>
                <Chip tone="ok">Aktif {s.aktif}</Chip>
              </div>

              {s.menunggu_konfirmasi > 0 && (
                <p className="text-xs text-muted-foreground">
                  {s.menunggu_konfirmasi} akun senilai {uang(s.nilai_menunggu_konfirmasi)} dilaporkan sudah dibayar sekolah
                  {s.claim_baru > 0 ? " (laporan dari Admin Sekolah)" : ""}. Konfirmasi setelah uangnya benar-benar masuk ke rekening/QRIS Anda.
                </p>
              )}
              {s.menunggu_aktivasi > 0 && (
                <p className="text-xs text-muted-foreground">
                  {s.menunggu_aktivasi_diminta > 0
                    ? `${s.menunggu_aktivasi_diminta} akun sudah Anda konfirmasi. Permintaan aktivasi sudah diteruskan ke founder — akun akan berubah menjadi Aktif setelah founder menyetujui.`
                    : `${s.menunggu_aktivasi} akun sudah ditandai sudah dibayar. Menunggu founder mengaktifkan akun.`}
                </p>
              )}
            </div>
          ))}
          {d.sekolah.length === 0 && <p className="text-sm text-muted-foreground">Belum ada sekolah binaan yang ditagih pada periode ini.</p>}
        </section>

        <section className="space-y-4">
          <div className="rounded-2xl border bg-card p-5 space-y-3" data-testid="mitra-payment-checkout">
            <h2 className="flex items-center gap-2 text-base font-bold">
              <QrCode className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Setor kulakan ke founder
            </h2>
            <p className="text-sm text-muted-foreground">
              Kulakan periode {d.periode}: <span className="font-semibold text-foreground">{uang(g.nominal_kulakan)}</span> untuk {g.akun} akun.
              Sudah terverifikasi {uang(g.terbayar_ke_founder)}{g.menunggu_verifikasi > 0 ? `, menunggu verifikasi ${uang(g.menunggu_verifikasi)}` : ""}.
            </p>
            {f.qris_image ? (
              <div className="rounded-xl border p-3 grid place-items-center bg-muted/40" data-testid="mitra-payment-founder-qris">
                <img src={f.qris_image} alt="QRIS founder" className="max-h-56 rounded-lg" />
              </div>
            ) : (
              <div className="rounded-xl border p-4 text-center bg-muted/40" data-testid="mitra-payment-founder-qris-empty">
                <QrCode className="h-6 w-6 text-muted-foreground mx-auto mb-1" aria-hidden />
                <p className="text-xs text-muted-foreground">Founder belum memasang QRIS. Gunakan kanal berikut.</p>
              </div>
            )}
            <p className="text-sm break-words">{founderKanal || "Kanal bayar founder belum diatur — hubungi Pusat Kendali."}</p>
            <div className="space-y-3 border-t pt-3">
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-sekolah">Sekolah</Label>
                <Select value={schoolId} onValueChange={(v) => { setSchoolId(v); setJumlah(""); }}>
                  <SelectTrigger id="mitra-setoran-sekolah" data-testid="mitra-setoran-school"><SelectValue placeholder="Pilih sekolah" /></SelectTrigger>
                  <SelectContent>
                    {perSekolah.map((s) => (
                      <SelectItem key={s.school_id} value={s.school_id}>
                        {s.school_name} ({s.akun} akun · {uang(s.nominal_kulakan)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sekolahTerpilih && (
                <div className="rounded-xl border p-3 bg-muted/30 text-sm space-y-1" data-testid="mitra-setoran-rincian">
                  <p className="font-semibold">{sekolahTerpilih.school_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {sekolahTerpilih.akun} akun · kulakan {uang(sekolahTerpilih.nominal_kulakan)} · sudah disetor {uang(sekolahTerpilih.sudah_disetor || 0)}
                  </p>
                  <p className="text-xs font-medium text-[#E11D2E]">Sisa: {uang(sisaSekolah)}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-jumlah">Jumlah setoran (rupiah)</Label>
                <Input id="mitra-setoran-jumlah" inputMode="numeric" placeholder={String(sisaSekolah || "")}
                  value={jumlah} onChange={(e) => setJumlah(e.target.value)} data-testid="mitra-setoran-amount" />
                <p className="text-xs text-muted-foreground">Contoh: {uang(sisaSekolah || g.sisa_kulakan)}.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-metode">Metode</Label>
                <Select value={metode} onValueChange={setMetode}>
                  <SelectTrigger id="mitra-setoran-metode" data-testid="mitra-setoran-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="qris">QRIS</SelectItem>
                    <SelectItem value="transfer">Transfer bank</SelectItem>
                    <SelectItem value="tunai">Tunai</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mitra-setoran-catatan">Catatan (opsional)</Label>
                <Input id="mitra-setoran-catatan" placeholder="Contoh: transfer 4 Nov via BCA" value={catatan}
                  onChange={(e) => setCatatan(e.target.value)} data-testid="mitra-setoran-note" />
              </div>
              <Button onClick={setor} disabled={busy === "setor"} data-testid="mitra-setoran-submit">
                {busy === "setor" ? <Spinner /> : <Send className="h-4 w-4 mr-1.5" />} Laporkan setoran
              </Button>
              <p className="text-xs text-muted-foreground">Laporan Anda diverifikasi founder dulu sebelum dihitung lunas.</p>
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-5 space-y-3" data-testid="mitra-payment-history">
            <h2 className="flex items-center gap-2 text-base font-bold"><Landmark className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Riwayat setoran {d.periode}</h2>
            {g.setoran.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada setoran pada periode ini.</p>
            ) : (
              <ul className="space-y-2">
                {g.setoran.map((s) => (
                  <li key={s.id} className="rounded-xl border p-3 flex flex-wrap items-center justify-between gap-2"
                      data-testid={`mitra-payment-setoran-${s.id}`}>
                    <div>
                      <p className="font-semibold tabular-nums">{uang(s.amount_idr)}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.rincian?.school_name && <span className="font-medium text-foreground">{s.rincian.school_name} · </span>}
                        {s.metode}{s.catatan ? ` · ${s.catatan}` : ""} · {new Date(s.at).toLocaleDateString("id-ID", { day: "numeric", month: "short" })}
                      </p>
                    </div>
                    <StatusBadge map={SETTLEMENT_STATUS} value={s.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <Dialog open={!!konfirmasiUntuk} onOpenChange={(o) => !o && tutupKonfirmasi()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="mitra-konfirmasi-dialog">
          <DialogHeader>
            <DialogTitle>
              Konfirmasi pembayaran {konfirmasiUntuk?.school_name}
              {akunMelapor.length > 0 ? ` · ${akunDipilih.length} dari ${akunMelapor.length} akun` : ""}
            </DialogTitle>
            <DialogDescription>
              Centang akun yang uangnya benar-benar sudah masuk ke rekening/QRIS Anda. Setelah dikonfirmasi,
              permintaan aktivasi diteruskan ke founder — akun belum otomatis aktif.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl border bg-muted/30 p-4 text-sm space-y-1" data-testid="mitra-konfirmasi-rincian">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Akun yang akan dikonfirmasi</span>
                <span className="font-semibold tabular-nums">
                  {akunDipilih.length} dari {akunMelapor.length} akun
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Nilai jual yang Anda terima</span>
                <span className="font-semibold tabular-nums">{uang(nilaiDipilih)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Periode</span>
                <span className="font-semibold">{d.periode}</span>
              </div>
            </div>
            <div className="rounded-xl border" data-testid="mitra-konfirmasi-akun">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2">
                <p className="text-sm font-semibold">Akun yang melapor sudah bayar ({akunMelapor.length})</p>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium" htmlFor="mitra-konfirmasi-pilih-semua">
                  <input id="mitra-konfirmasi-pilih-semua" type="checkbox"
                    className="h-4 w-4 accent-[#3B82F6]" checked={semuaDipilih} onChange={toggleSemua}
                    disabled={!akunMelapor.length} data-testid="mitra-konfirmasi-pilih-semua" />
                  Pilih semua
                </label>
              </div>
              <ul className="max-h-48 divide-y overflow-y-auto text-sm">
                {akunMelapor.map((b) => (
                  <li key={b.id} className="px-3 py-1.5" data-testid={`mitra-konfirmasi-akun-${b.id}`}>
                    <label className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5" htmlFor={`mitra-konfirmasi-pilih-${b.id}`}>
                      <input id={`mitra-konfirmasi-pilih-${b.id}`} type="checkbox"
                        className="h-4 w-4 shrink-0 accent-[#3B82F6]" checked={dipilih.includes(b.id)}
                        onChange={() => toggleAkun(b.id)} data-testid={`mitra-konfirmasi-pilih-${b.id}`} />
                      <span className="font-medium">{b.full_name || b.email}</span>
                      <span className="text-xs text-muted-foreground">{ROLE_LABELS[b.role] || b.role || "Akun sekolah"}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{formatDate(b.claim?.pada)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            {akunDipilih.length > 0 && akunDipilih.length < akunMelapor.length && (
              <Notice tone="warn" testId="mitra-konfirmasi-sebagian">
                Hanya {akunDipilih.length} akun yang akan dikonfirmasi. {akunMelapor.length - akunDipilih.length} akun
                lain tetap menunggu konfirmasi Anda.
              </Notice>
            )}
            {akunSudahDikonfirmasi.length > 0 && (
              <div className="rounded-xl border" data-testid="mitra-konfirmasi-sudah">
                <p className="border-b bg-muted/40 px-3 py-2 text-sm font-semibold">
                  Sudah Anda konfirmasi, menunggu aktivasi founder ({akunSudahDikonfirmasi.length})
                </p>
                <ul className="max-h-40 divide-y overflow-y-auto text-sm">
                  {akunSudahDikonfirmasi.map((b) => (
                    <li key={b.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1.5"
                      data-testid={`mitra-konfirmasi-sudah-${b.id}`}>
                      <span className="font-medium">{b.full_name || b.email}</span>
                      <span className="text-xs text-muted-foreground">{ROLE_LABELS[b.role] || b.role || "Akun sekolah"}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{formatDate(b.activation_requested_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-muted-foreground">
              <li>Status akun berubah menjadi “sudah dibayar ke Anda”.</li>
              <li>Permintaan aktivasi masuk ke antrean founder dan notifikasi dikirim ke Pusat Kendali.</li>
              <li>Founder menekan Aktifkan — baru setelah itu akun berstatus Aktif dan tidak bisa terkunci tanggal.</li>
            </ol>
            <label className="flex items-start gap-2 rounded-xl border p-3 text-sm cursor-pointer" htmlFor="mitra-konfirmasi-cocok">
              <input id="mitra-konfirmasi-cocok" type="checkbox" className="mt-0.5 h-4 w-4 accent-[#3B82F6]"
                checked={sudahCocok} onChange={(e) => setSudahCocok(e.target.checked)}
                data-testid="mitra-konfirmasi-checkbox" />
              <span>Saya sudah mencocokkan mutasi rekening/QRIS dan uangnya benar-benar masuk.</span>
            </label>
            {err && <Notice tone="danger" testId="mitra-konfirmasi-error">{err}</Notice>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={tutupKonfirmasi} disabled={busy === konfirmasiUntuk?.school_id}
              data-testid="mitra-konfirmasi-cancel">Batal</Button>
            <Button onClick={konfirmasi}
              disabled={!sudahCocok || !akunDipilih.length || busy === konfirmasiUntuk?.school_id}
              data-testid="mitra-konfirmasi-submit">
              {busy === konfirmasiUntuk?.school_id ? <Spinner /> : <BadgeCheck className="h-4 w-4 mr-1.5" />}
              Ya, konfirmasi {akunDipilih.length} akun
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!hasilKonfirmasi} onOpenChange={(o) => !o && setHasilKonfirmasi(null)}>
        <DialogContent data-testid="mitra-konfirmasi-hasil-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden /> Konfirmasi terkirim
            </DialogTitle>
            <DialogDescription>
              {hasilKonfirmasi?.dikonfirmasi} akun dari {hasilKonfirmasi?.school_name} senilai{" "}
              {uang(hasilKonfirmasi?.nominal_jual)} sudah Anda konfirmasi untuk periode {hasilKonfirmasi?.periode}.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
            data-testid="mitra-konfirmasi-hasil-next">
            <p className="font-semibold">Langkah berikutnya</p>
            <p className="mt-1">
              Permintaan aktivasi sudah diteruskan ke founder. Kartu sekolah sekarang berlabel
              “Menunggu aktivasi founder”; label itu berubah menjadi “Aktif” setelah founder menyetujui.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setHasilKonfirmasi(null)} data-testid="mitra-konfirmasi-hasil-close">Mengerti</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
